import React, { useEffect, useState } from 'react'
import { Box, Flex, Text } from 'rebass/styled-components'
import { Input } from '@rebass/forms/styled-components'
import styled from 'styled-components'
import axios from 'axios-jsonp-pro'
import AnimationWrapper from '../helpers/Animations'
import { Link } from 'gatsby'
import { useInView } from 'react-intersection-observer'

const NewsletterDialogOverlay = styled(Box)`
    display: none;

    &[data-visible=true] {
        display: block;
    }
`
const NewsletterDialogForm = styled(Flex)` 
    display: none;
    
    &[data-visible=true] {
        display: flex;
    }

    a {
        background-color: transparent;
        background-image: linear-gradient(to right, #101010 0, #101010 100%);
        background-position: 0 100%;
        background-repeat: no-repeat;
        background-size: 0 1px;
        border-color: ${(props) => props.theme.colors.aquaDark};
        border-style: solid;
        border-width: 0 0 1px 0;
        color: ${(props) => props.theme.colors.aquaDark};
        cursor: pointer;
        display: inline;
        padding-bottom: 1px;
        padding-top: 1px;
        will-change: background-size;
        text-decoration: none;

        &:hover {
            background-size: 100% 1px;
            border-color: transparent;
            padding-bottom: 2px;
            transition-duration: 400ms;
            transition-property: background-size;
            transition-timing-function: cubic-bezier(.2,.75,.5,1.0);
        }
    }
    
    input[type=text],
    input[type=email] {
        border-color: ${(props) => props.theme.colors.black};
        border-width: 1px;
        color: ${(props) => props.theme.colors.black};
        font-family: ${(props) => props.theme.fonts.body};
        margin-top: ${(props) => props.theme.space.sm};
        padding-bottom: 10px;
        padding-left: 8px;
        padding-right: 8px;
        padding-top: 10px;
        opacity: 0.6;

        transition-duration: 100ms, 200ms;
        transition-timing-function: ease-out, ease-out;
        transition-property: border-color, opacity;

        will-change: border-color, opacity;

        &:hover {
            opacity: 1.0;
        }

        &:focus {
            border-color: ${(props) => props.theme.colors.aquaDark};
            outline: none;
        }
    }

    input[type=submit] {
        background-color: ${(props) => props.theme.colors.aquaDark};
        color: ${(props) => props.theme.colors.black};
        font-weight: ${(props) => props.theme.fontWeights.semibold};
        margin-top: ${(props) => props.theme.space.sm};
        transition-duration: 150ms, 150ms;
        transition-property: background-color, color;
        transition-timing-function: ease-out, ease-out;
        will-change: background-color, color;

        &:hover {
            background-color: ${(props) => props.theme.colors.black};
            color: ${(props) => props.theme.colors.white};
        }
    }

    button[data-action=close] {
        align-content: center;
        align-items: center;
        align-self: center;
        background-color: ${(props) => props.theme.colors.black};
        border: none;
        color: transparent;
        cursor: pointer;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        font-size: 10px;
        height: 28px;
        justify-content: center;
        line-height: 28px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        right: ${(props) => props.theme.space.xs};
        text-align: center;
        text-indent: -9999px;
        top: ${(props) => props.theme.space.xs};
        width: 28px;

        &:before,
        &:after {
            background-color: ${(props) => props.theme.colors.white};
            content: '';
            display: block;
            height: 100%;
            left: calc(50% - 1px);
            position: absolute;
            top: 0;
            transform: scale(0.70) rotate(45deg);
            transform-origin: 50% 50%;
            width: 2px;
            will-change: transform;
        }

        &:after {
            transform: scale(0.70) rotate(-45deg);
        }

        &:hover:before,
        &:hover:after {
            transition-duration: 150ms;
            transition-property: transform;
            transition-timing-function: ease-out;
        }

        &:hover:before {
            transform: scale(0.5) rotate(45deg);
        }
        &:hover:after {
            transform: scale(0.5) rotate(-45deg);
        }
    }
`

const NewsletterDialog = () => {
    const url = typeof window !== 'undefined' ? window.location.href : 'Referring URL not available'
    const [response, setResponse] = useState(null)

    const [ref, inView] = useInView()
    const [isVisible, setVisible] = useState({ visible: false, enabled: false })

    const cookieSet = (name, value, days) => {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days*24*60*60*1000));
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/';
    }

    const cookieGet = (name) => {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i=0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0)==' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    useEffect(()=> {
        const hasSubscribed = cookieGet('NewsletterDialogSubscribed')
        // console.log(`hasSubscribed: ${hasSubscribed}`)
        if (!isVisible.enabled) {
            // Timeout to account for page load to prevent showing on load.
            // TODO: experiment with Suspense to replace timeout hack

            const isHomepage = (window?.location.pathname === '/' ) ? true : false
            // console.log(`NewsletterDialog: isHomepage = ${isHomepage}`)

            if (isHomepage === true) {
                const delay = 1000
                setTimeout(() => {
                    // console.log('NewsletterDialog: setTimeout fired')
                    setVisible({ ...isVisible, enabled: true });
                }, delay)
            }
        }

        if (inView && !isVisible.visible && isVisible.enabled && hasSubscribed !== 'true') {
            setVisible({ ...isVisible, visible: inView })
        }
    })

    const signUp = (e) => {
        e.preventDefault()
        const data = new FormData(e.target)
        const name = data.get('name')
        const email = data.get('email')
        axios.jsonp('https://kidston.createsend.com/t/d/s/ahujij', {
            params: {
                'cm-name': name,
                'cm-ahujij-ahujij': email,
            },
        }).then((res) => {
            const { Status } = res
            const { Message } = res
            const output = <Box
                pt="sm"
            >
                <AnimationWrapper type='fade-up'>
                    <Text
                        as="p"
                        color={Status === 400 ? 'errorDark' : 'black'}
                        fontSize="sm"
                    >
                        {Message}
                    </Text>
                </AnimationWrapper>
            </Box>
            setResponse(output)
            if (Status !== 400) {
                cookieSet('NewsletterDialogSubscribed', 'true', 1);
            }
        })
    }

    const closeNewsletterDialog = () => {
        cookieSet('NewsletterDialogSubscribed', 'true', 1);
        setVisible({ ...isVisible, visible: false });
    };

    return (
        <>
            <Box ref={ref}></Box>
            <NewsletterDialogOverlay
                data-visible={isVisible.visible && isVisible.enabled ? 'true' : 'false'} onClick={closeNewsletterDialog}
                sx={{
                    backgroundColor: 'rgba(0,0,0,0.80)',
                    height: '100vh',
                    left: '0',
                    position: 'fixed',
                    top: '0',
                    width: '100%',
                    zIndex: '1000',
                }}
            />
            <NewsletterDialogForm
                data-visible={isVisible.visible && isVisible.enabled ? 'true' : 'false'}
                alignContent="center"
                alignItems={['flex-start', 'flex-start', 'center', 'center', 'center', 'center', 'center']}
                justifyContent="center"
                px={['xs', 'xs', '0', '0', '0', '0', '0']}
                sx={{
                    height: '100vh',
                    left: '0',
                    opacity: '1.0',
                    pointerEvents: 'none',
                    position: 'fixed',
                    top: '0',
                    width: '100%',
                    zIndex: '1001',
                }}
            >
                <Box
                    mb={['0', '0', '10%', '10%', '10%', '10%', '10%']}
                    ml="auto"
                    mr="auto"
                    pl={['xxs', 'xxs', 'md', 'md', 'md', 'md', 'md']}
                    pr={['xxs', 'xxs', 'md', 'md', 'md', 'md', 'md']}
                    maxWidth="480px"
                    sx={{
                        pointerEvents: 'auto',
                    }}>
                    <Box
                        sx={{
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.95)',
                        }}>
                        <Box
                            as="form"
                            bg="white"
                            onSubmit={(e) => signUp(e)}
                            role="form"
                            aria-label="Subscribe to our Newsletter"
                            autoComplete="on"
                            pb="sm"
                            pt="sm"
                            pr="sm"
                            pl="sm"
                            sx={{
                                position: 'relative',
                            }}
                        >
                            <button
                                data-action="close"
                                type="button"
                                onClick={closeNewsletterDialog}
                            >Close</button>
                            <Input type="hidden" name="referrer" value={url} />
                            <Text
                                as="label"
                                color="black"
                                htmlFor="name-NewsletterDialog-signup"
                                mt="xs"
                                pb="xs"
                                pr="xs"
                                variant="h5"
                                sx={{
                                    borderColor: 'black',
                                    borderStyle: 'solid',
                                    borderWidth: '0 0 1px 0',
                                    cursor: 'pointer',
                                    display: 'block',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Keep Ahead of the Pack
                            </Text>
                            <Text
                                as="p"
                                fontSize="sm"
                                pt="sm"
                            >
                                Join our mailing list and be the first to hear our latest news, biggest announcements and exclusive content.
                            </Text>
                            <Text
                                as="label"
                                htmlFor="name-NewsletterDialog-signup"
                                hidden
                            >
                                Full name
                            </Text>
                            <Input
                                autoComplete="name"
                                enterKeyHint="next"
                                id="name-NewsletterDialog-signup"
                                name="name"
                                placeholder="Full name"
                                type="text"
                            />
                            <Text
                                as="label"
                                htmlFor="email-NewsletterDialog-signup"
                                hidden
                            >
                                Email Address
                            </Text>
                            <Input
                                autoComplete="email"
                                enterKeyHint="done"
                                id="email-NewsletterDialog-signup"
                                name="email"
                                placeholder="Email Address"
                                type="email"
                            />
                            <Input variant="darkMode.submit" aria-pressed="false" type="submit" value="Join" />

                            { response }

                            <Text
                                as="p"
                                fontSize="sm"
                                pt="sm"
                            >
                                Kidston collects personal information when you subscribe to our mailing list. By signing up you agree to our <Link to="/privacy/">privacy policy</Link>. You can unsubscribe at any time.
                            </Text>
                        </Box>
                        <Box
                            height="220px"
                            sx={{
                                backgroundImage: 'url(https://res.cloudinary.com/kidston/image/upload/v1622719594/website/slideshow/AS7I7438.jpg)',
                                backgroundPosition: '50% 50%',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'cover',
                            }}
                        >
                        </Box>
                    </Box>
                </Box>
            </NewsletterDialogForm>
        </>
    )
}

export default NewsletterDialog
